import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarPlus, ChevronDown, ChevronUp, Sparkles, X } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { todayISO } from '../lib/dates'
import {
  WEEKEND_LOOKBACK_MONTHS, buildPriceAdvice,
  type AdviceSpan, type HolidayDensityDay,
} from '../lib/priceAdvice'
import type { ISODate } from '../data/model'
import { cx } from '../ui/kit'
import {
  SeasonProposalSheet, spanProposal, weekendProposal, type SeasonProposal,
} from './PriceAdviceCard'

/**
 * Die Preisempfehlungen, dort wo der Vermieter ohnehin jeden Tag hinschaut.
 *
 * WARUM NICHT IN DEN EINSTELLUNGEN: dort war die Karte schon, und dort ist sie
 * unsichtbar. In die Einstellungen geht man dreimal — beim Einrichten, beim
 * Steuerberaterwechsel und wenn etwas kaputt ist. Ein Hinweis, der eine Preisrunde
 * anstoßen soll, muss neben dem Kalender stehen, in dem man die Lücke sieht.
 *
 * WARUM EINE ZEILE UND KEIN KASTEN: der Kalender ist der Arbeitsplatz. Alles, was ihn
 * dauerhaft kleiner macht, wird zu Recht weggeklickt. Eingeklappt ist es ein Satz;
 * aufgeklappt so lang wie nötig, und das entscheidet der Vermieter.
 *
 * WARUM ES SICH ABSCHALTEN LÄSST: weil es sonst nervt. Wer seine Preise anders macht,
 * klickt einmal auf das Kreuz und sieht die Zeile nie wieder — die Einstellung liegt
 * beim Betrieb und nicht im Browser, damit sie auch auf dem Handy weg bleibt.
 *
 * WARUM DERSELBE KNOPF WIE IN DEN EINSTELLUNGEN: der Vorschlag entsteht hier, also
 * muss er auch hier umsetzbar sein. Das Bestätigungsblatt kommt aus PriceAdviceCard —
 * eine zweite Fassung derselben Frage wäre eine zweite Fassung derselben Fehler.
 */
export default function PriceAdviceBar({
  holidaysByDay, onJump,
}: {
  holidaysByDay?: ReadonlyMap<ISODate, HolidayDensityDay>
  /** Springt im Kalender an den Zeitraumbeginn. */
  onJump?: (weekStart: ISODate) => void
}) {
  const { lang, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const navigate = useNavigate()
  const de = lang === 'de'
  const [open, setOpen] = useState(false)
  const [proposal, setProposal] = useState<SeasonProposal | null>(null)

  const result = useMemo(() => buildPriceAdvice({
    today: todayISO(),
    units: store.db.units,
    bookings: store.db.bookings,
    ratePeriods: store.db.rate_periods,
    holidaysByDay,
  }), [store.db.units, store.db.bookings, store.db.rate_periods, holidaysByDay])

  if (store.db.tenant.price_advice_enabled === false) return null
  if (result.spans.length === 0 && !result.weekend) return null

  // Ein Satz, der sagt, was zu holen ist — nicht, wie viele Hinweise es gibt.
  const raises = result.spans.filter((s) => s.suggestedPct != null)
  const gaps = result.spans.filter((s) => s.kind === 'gap')
  const headline = raises.length > 0
    ? (de
        ? `${raises.length === 1 ? 'Ein Zeitraum ist' : `${raises.length} Zeiträume sind`} gut gebucht — der Preis könnte höher sein`
        : `${raises.length === 1 ? 'One period is' : `${raises.length} periods are`} well booked — the price could be higher`)
    : result.weekend
      ? (de
          ? 'Ihre Wochenenden laufen deutlich besser als die Wochentage'
          : 'Your weekends run considerably better than your weekdays')
      : gaps.length > 0
        ? (de
            ? `${gaps.length === 1 ? 'Ein Zeitraum füllt' : `${gaps.length} Zeiträume füllen`} sich nicht`
            : `${gaps.length === 1 ? 'One period is' : `${gaps.length} periods are`} not filling up`)
        : (de ? 'Für einige Zeiträume greift nur der Grundpreis' : 'Some periods fall back to the base rate')

  return (
    <div className="no-print border-b border-border bg-accent-soft/60">
      <div className="px-4 py-2 flex items-center gap-2 text-sm">
        <Sparkles size={15} className="text-accent shrink-0" />
        <button className="flex-1 min-w-0 text-left truncate" onClick={() => setOpen(!open)}>
          <span className="text-accent font-medium">{headline}</span>
        </button>
        <button onClick={() => setOpen(!open)} className="text-accent shrink-0"
          aria-label={open ? (de ? 'Zuklappen' : 'Collapse') : (de ? 'Aufklappen' : 'Expand')}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        <button
          className="text-muted hover:text-fg shrink-0"
          aria-label={de ? 'Preisempfehlungen abschalten' : 'Turn off price suggestions'}
          title={de ? 'Nicht mehr anzeigen' : 'Stop showing this'}
          onClick={() => {
            if (!window.confirm(de
              ? 'Preisempfehlungen nicht mehr anzeigen? In den Einstellungen unter „Preise" lassen sie sich wieder einschalten.'
              : 'Stop showing price suggestions? You can switch them back on under Settings → Rates.')) return
            void store.saveTenant({ price_advice_enabled: false })
          }}
        >
          <X size={15} />
        </button>
      </div>

      {open && (
        <ul className="px-4 pb-3 space-y-1.5">
          {result.spans.map((s) => (
            <li key={s.from} className="rounded-xl bg-surface overflow-hidden">
              <button
                className="w-full text-left px-3 py-2 hover:bg-surface-2"
                onClick={() => {
                  onJump?.(s.from)
                  setOpen(false)
                }}
              >
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="font-medium">{weekLabel(s, de)}</span>
                  <span className="text-muted text-xs truncate">
                    {formatDate(s.from)} – {formatDate(s.lastDay)}
                  </span>
                  <span className="ml-auto tabular-nums text-xs text-muted">
                    {Math.round(s.occupancy * 100)} %
                    {s.averagePrice > 0 && ` · ⌀ ${formatMoney(Math.round(s.averagePrice))}`}
                  </span>
                  {s.suggestedPct != null && (
                    <span className={cx('text-xs font-medium rounded-lg px-1.5 py-0.5',
                      'bg-ok-soft text-ok')}>
                      +{s.suggestedPct} %
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted mt-0.5">{reason(s, de)}</p>
              </button>
              {s.suggestedPrice != null && (
                <button
                  className="w-full text-left px-3 py-2 border-t border-border text-xs
                    text-accent font-medium flex items-center gap-1.5 hover:bg-surface-2"
                  onClick={() => setProposal(spanProposal(s, de))}
                >
                  <CalendarPlus size={14} />
                  {de
                    ? `Als Saison anlegen — ${formatMoney(s.suggestedPrice)}/Nacht`
                    : `Create as a season — ${formatMoney(s.suggestedPrice)}/night`}
                </button>
              )}
            </li>
          ))}

          {result.weekend && (
            <li className="rounded-xl bg-surface overflow-hidden">
              <div className="px-3 py-2">
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="font-medium">
                    {de ? 'Wochenende' : 'Weekend'}
                  </span>
                  <span className="ml-auto tabular-nums text-xs text-muted">
                    {Math.round(result.weekend.weekendOccupancy * 100)} %
                    {' · Mo–Do '}
                    {Math.round(result.weekend.weekdayOccupancy * 100)} %
                  </span>
                  <span className={cx('text-xs font-medium rounded-lg px-1.5 py-0.5',
                    'bg-ok-soft text-ok')}>
                    +{result.weekend.suggestedPct} %
                  </span>
                </div>
                <p className="text-xs text-muted mt-0.5">
                  {de
                    ? `Fr und Sa waren in den letzten ${WEEKEND_LOOKBACK_MONTHS} Monaten deutlich besser gebucht als Mo bis Do — einen Zuschlag dafür gibt es noch nicht.`
                    : `Fri and Sat were booked considerably better than Mon–Thu over the past ${WEEKEND_LOOKBACK_MONTHS} months — and there is no surcharge for them yet.`}
                </p>
              </div>
              <button
                className="w-full text-left px-3 py-2 border-t border-border text-xs
                  text-accent font-medium flex items-center gap-1.5 hover:bg-surface-2"
                onClick={() => setProposal(weekendProposal(result.weekend!, de))}
              >
                <CalendarPlus size={14} />
                {de
                  ? `Zuschlag anlegen — ${formatMoney(result.weekend.suggestedPrice)}/Nacht an Fr und Sa`
                  : `Add the surcharge — ${formatMoney(result.weekend.suggestedPrice)}/night on Fri and Sat`}
              </button>
            </li>
          )}

          <li className="pt-1 flex items-center gap-3 text-xs">
            <button className="underline underline-offset-2 text-accent"
              onClick={() => navigate('/settings')}>
              {de ? 'Saisonpreise bearbeiten' : 'Edit seasonal rates'}
            </button>
            <span className="text-muted">
              {de
                ? 'Vorschläge aus Auslastung und Ferienlage — keine Marktpreisberechnung.'
                : 'Suggestions from occupancy and school holidays — not a market price calculation.'}
            </span>
          </li>
        </ul>
      )}

      <SeasonProposalSheet proposal={proposal} onClose={() => setProposal(null)} />
    </div>
  )
}

/** „KW 30–32" bzw. „KW 32". */
function weekLabel(span: AdviceSpan, de: boolean): string {
  const first = span.weeks[0]!.isoWeek
  const last = span.weeks[span.weeks.length - 1]!.isoWeek
  const prefix = de ? 'KW' : 'Week'
  return first === last ? `${prefix} ${first}` : `${prefix} ${first}–${last}`
}

/** Ein Satz, warum dieser Zeitraum auffällt. */
function reason(s: AdviceSpan, de: boolean): string {
  if (s.kind === 'gap') {
    return de
      ? `Nur ${Math.round(s.occupancy * 100)} % belegt und schon nah — ein Angebot oder ein niedrigerer Preis könnte helfen.`
      : `Only ${Math.round(s.occupancy * 100)}% booked and close at hand — an offer or a lower price might help.`
  }
  if (s.kind === 'no_season') {
    return de
      ? 'Für diesen Zeitraum ist keine Saison hinterlegt, es greift der Grundpreis.'
      : 'No season covers this period, so the base rate applies.'
  }
  const parts = [
    de
      ? `${Math.round(s.occupancy * 100)} % belegt`
      : `${Math.round(s.occupancy * 100)}% booked`,
  ]
  if (s.holidayRegions.length > 0) {
    parts.push(de
      ? `Ferien in ${s.holidayRegions.slice(0, 2).join(' und ')}`
      : `school holidays in ${s.holidayRegions.slice(0, 2).join(' and ')}`)
  }
  return parts.join(' · ')
}
